<template>
    <div class="container-fluid ppv2-p-6 ppv2-p-md-5">
        <div class="row g-4 g-lg-5">
            <!-- ================== Left Panel: Controls ================== -->
            <div class="col-lg-6 d-flex flex-column mt-0">
                <div class="h-100 pe-lg-3">
                    <Header
                        :title="$t('widgetIntegration.stepHeaders.' + currentStepKey + '.title')"
                        :subtitle="$t('widgetIntegration.stepHeaders.' + currentStepKey + '.description')"
                    />

                    <div class="widget-options d-flex flex-column gap-3 mt-4 flex-grow-1">
                        <!-- Step 1: Show widget selection -->
                        <div v-if="currentStep === 1">
                            <div
                                v-for="widget in widgets"
                                :key="widget.id"
                                class="widget-card p-4 border rounded-3"
                                :class="{ 'active-card': selectedWidget && selectedWidget.id === widget.id }"
                                @click="selectWidget(widget)"
                            >
                                <div class="d-flex align-items-center">
                                    <div class="selection-icon me-4">
                                        <svg
                                            v-if="selectedWidget && selectedWidget.id === widget.id"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                                                fill="#7D2DBD"
                                            />
                                        </svg>
                                        <svg
                                            v-else
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="12" cy="12" r="10" stroke="#a4b0be" stroke-width="2" />
                                        </svg>
                                    </div>
                                    <div class="text-content">
                                        <h3 class="fs-5 fw-semibold mb-1">{{ widget.title }}</h3>
                                        <p class="text-secondary mb-0">{{ widget.description }}</p>
                                    </div>
                                    <div class="widget-icon ms-auto" v-html="widget.iconSvg"></div>
                                </div>
                            </div>
                        </div>

                        <!-- Step 2: Show activity select -->
                        <div v-if="currentStep === 2">
                            <BaseSelect
                                v-model="selectedActivityId"
                                :label="$t('widgetIntegration.selectActivity')"
                                :options="activities"
                                :placeholder="$t('widgetIntegration.chooseActivity')"
                            />

                            <!-- Bloc d'actions rapides -->
                            <div class="activity-actions-box mt-4">
                                <p class="text-secondary mb-3">{{ $t('widgetIntegration.needChange') }}</p>
                                <div class="d-flex flex-column gap-2">
                                    <div class="action-link-wrapper">
                                        <a
                                            :href="selectedItem ? getEditUrl(selectedItem) : '#'"
                                            target="_blank"
                                            :class="{ 'disabled-link': !selectedActivityId }"
                                            @click="handleModifyClick"
                                        >
                                            <svg
                                                class="action-icon"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                            </svg>
                                            <span>{{ $t(actionKeys.modify) }}</span>
                                        </a>
                                    </div>
                                    <div class="action-link-wrapper">
                                        <a
                                            href="/back-office/v3/experience/create-simple"
                                            target="_blank"
                                            @click="handleCreateClick"
                                        >
                                            <svg
                                                class="action-icon"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                            </svg>
                                            <span>{{ $t(actionKeys.create) }}</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Navigation buttons -->
                        <div class="d-flex gap-3">
                            <button
                                v-if="isBackButtonVisible"
                                class="d-flex align-items-center gap-2"
                                style="color: #3e4254"
                                @click="handleBackClick"
                            >
                                <svg
                                    class="action-icon"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path d="M19 12H5m7 7l-7-7 7-7"></path>
                                </svg>
                                {{ $t('widgetIntegration.back') }}
                            </button>
                            <BaseButton
                                v-if="isActionButtonVisible"
                                variant="primary"
                                :loading="submitting"
                                @click="handleActionButtonClick"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3] ms-auto"
                            >
                                {{ buttonText }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ================== Right Panel: Preview ================== -->
            <div class="col-lg-6 d-flex flex-column mt-0">
                <div class="h-100 ps-lg-3">
                    <div class="preview-header d-flex align-items-center gap-2 mb-3 text-secondary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span class="fw-medium">{{ $t('widgetIntegration.preview') }}</span>
                    </div>

                    <div
                        class="iframe-wrapper flex-grow-1 bg-white border ppv2-p-6 rounded-4 shadow-sm overflow-hidden position-relative"
                        :class="{ loading: isLoadingIframe }"
                    >
                        <div v-if="isLoadingIframe" class="loading-overlay">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Chargement...</span>
                            </div>
                        </div>
                        <div
                            v-if="!isLoadingIframe && !activityIsValid && selectedWidget.id === 'activite'"
                            class="error-overlay"
                        >
                            <div class="text-center">
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="text-danger mb-3"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="15" y1="9" x2="9" y2="15"></line>
                                    <line x1="9" y1="9" x2="15" y2="15"></line>
                                </svg>
                                <h5 class="text-danger">{{ $t('widgetIntegration.activityNotAccessible') }}</h5>
                                <p class="text-muted">{{ activityError }}</p>
                            </div>
                        </div>
                        <iframe
                            v-if="iframeSrc && activityIsValid"
                            :key="iframeSrc"
                            :src="iframeSrc"
                            frameborder="0"
                            class="w-100 ppv2-h-[84vh]"
                            @load="onIframeLoad"
                        ></iframe>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modale pour afficher le code généré -->
        <CodeModal v-if="isModalVisible" :code-snippet="generatedCodeSnippet" @close="isModalVisible = false" />
    </div>
</template>

<script>
import axios from 'axios'
import BaseSelect from './Shared/BaseSelect.vue'
import BaseButton from './Shared/BaseButton.vue'
import CodeModal from './CodeModal.vue' // Assurez-vous que le chemin est correct
import Header from './Shared/Header.vue'
export default {
    props: ['url_front', 'url_back', 'lang'],
    components: {
        BaseSelect,
        BaseButton,
        Header,
        CodeModal,
    },
    data() {
        return {
            selectedWidget: null,
            currentStep: 1,
            activities: [],
            selectedActivityId: null,
            isLoadingIframe: true,
            isModalVisible: false,
            generatedCodeSnippet: '',
            activityError: null,
            activityIsValid: true,
            submitting: false,

            widgets: [
                {
                    id: 'listing',
                    title: 'Listing des expériences',
                    description: 'Listing de vos expériences, de la réservation au paiement.',
                    url_front: `${this.url_front}/discover/reservation`,
                    needsActivitySelection: false,
                    stepHeaders: {
                        1: {
                            title: 'Intégration widgets',
                            description:
                                "Les widgets vous permettent d'intégrer nos modules de réservation et bon cadeau directement sur votre propre site Internet.",
                        },
                        2: {
                            title: "Sélection d'activité",
                            description: "Choisissez l'activité que vous souhaitez intégrer dans votre widget.",
                        },
                    },
                    iconSvg: `<svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M26.25 7.5H39.375M26.25 16.875H39.375M26.25 28.125H39.375M26.25 37.5H39.375M7.5 5.625H16.875C17.9105 5.625 18.75 6.46447 18.75 7.5V16.875C18.75 17.9105 17.9105 18.75 16.875 18.75H7.5C6.46447 18.75 5.625 17.9105 5.625 16.875V7.5C5.625 6.46447 6.46447 5.625 7.5 5.625ZM7.5 26.25H16.875C17.9105 26.25 18.75 27.0895 18.75 28.125V37.5C18.75 38.5355 17.9105 39.375 16.875 39.375H7.5C6.46447 39.375 5.625 38.5355 5.625 37.5V28.125C5.625 27.0895 6.46447 26.25 7.5 26.25Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
                },
                {
                    id: 'activite',
                    title: 'Expérience',
                    description: "Page de réservation de l'expérience de votre choix.",
                    url_front: `${this.url_front}/discover/reservation`,
                    needsActivitySelection: true,
                    stepHeaders: {
                        1: {
                            title: 'Intégration widgets',
                            description:
                                "Les widgets vous permettent d'intégrer nos modules de réservation et bon cadeau directement sur votre propre site Internet.",
                        },
                        2: {
                            title: 'Page de réservation',
                            description:
                                'Cette page présente les détails de l’expérience et permet de procéder à sa réservation jusqu’au paiement.',
                        },
                    },
                    iconSvg: `<svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.125 15H31.875M13.125 22.5H31.875M13.125 30H31.875M9.375 5.625H35.625C37.6961 5.625 39.375 7.30393 39.375 9.375V35.625C39.375 37.6961 37.6961 39.375 35.625 39.375H9.375C7.30393 39.375 5.625 37.6961 5.625 35.625V9.375C5.625 7.30393 7.30393 5.625 9.375 5.625Z" stroke="#131C31" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
                },
                {
                    id: 'bon-cadeau',
                    title: 'Bons cadeaux',
                    description: 'Page de vente des bons cadeaux.',
                    url_front: `${this.url_front}/gift`,
                    needsActivitySelection: false,
                    stepHeaders: {
                        1: {
                            title: 'Intégration widgets',
                            description:
                                "Les widgets vous permettent d'intégrer nos modules de réservation et bon cadeau directement sur votre propre site Internet.",
                        },
                        2: {
                            title: "Sélection d'activité",
                            description: "Choisissez l'activité que vous souhaitez intégrer dans votre widget.",
                        },
                    },
                    iconSvg: `<svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.5 15.0008V39.3758M22.5 15.0008C21.8218 12.206 20.654 9.81656 19.1489 8.14425C17.6438 6.47193 15.8713 5.59431 14.0625 5.62582C12.8193 5.62582 11.627 6.11968 10.7479 6.99876C9.86886 7.87783 9.375 9.07012 9.375 10.3133C9.375 11.5565 9.86886 12.7488 10.7479 13.6279C11.627 14.507 12.8193 15.0008 14.0625 15.0008M22.5 15.0008C23.1782 12.206 24.346 9.81656 25.8511 8.14425C27.3562 6.47193 29.1287 5.59431 30.9375 5.62582C32.1807 5.62582 33.373 6.11968 34.2521 6.99876C35.1311 7.87783 35.625 9.07012 35.625 10.3133C35.625 11.5565 35.1311 12.7488 34.2521 13.6279C33.373 14.507 32.1807 15.0008 30.9375 15.0008M35.625 22.5008V35.6258C35.625 36.6204 35.2299 37.5742 34.5266 38.2775C33.8234 38.9807 32.8696 39.3758 31.875 39.3758H13.125C12.1304 39.3758 11.1766 38.9807 10.4733 38.2775C9.77009 37.5742 9.375 36.6204 9.375 35.6258V22.5008M7.5 15.0008H37.5C38.5355 15.0008 39.375 15.8403 39.375 16.8758V20.6258C39.375 21.6614 38.5355 22.5008 37.5 22.5008H7.5C6.46447 22.5008 5.625 21.6614 5.625 20.6258V16.8758C5.625 15.8403 6.46447 15.0008 7.5 15.0008Z" stroke="#131C31" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
                },
                {
                    id: 'mon-compte',
                    title: 'Espace client',
                    description: "Donne accès à l’espace client et à l’historique d'achats.",
                    url_front: `${this.url_front}/connexion`,
                    needsActivitySelection: false,
                    stepHeaders: {
                        1: {
                            title: 'Intégration widgets',
                            description:
                                "Les widgets vous permettent d'intégrer nos modules de réservation et bon cadeau directement sur votre propre site Internet.",
                        },
                        2: {
                            title: "Sélection d'activité",
                            description: "Choisissez l'activité que vous souhaitez intégrer dans votre widget.",
                        },
                    },
                    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 45 45" fill="none"><path d="M35.625 5.625H9.375C7.30393 5.625 5.625 7.30393 5.625 9.375V35.625C5.625 37.6961 7.30393 39.375 9.375 39.375H35.625C37.6961 39.375 39.375 37.6961 39.375 35.625V9.375C39.375 7.30393 37.6961 5.625 35.625 5.625Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/><path d="M22.5 24.375C25.6066 24.375 28.125 21.8566 28.125 18.75C28.125 15.6434 25.6066 13.125 22.5 13.125C19.3934 13.125 16.875 15.6434 16.875 18.75C16.875 21.8566 19.3934 24.375 22.5 24.375Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.125 39.375V35.625C13.125 34.6304 13.5201 33.6766 14.2233 32.9734C14.9266 32.2701 15.8804 31.875 16.875 31.875H28.125C29.1196 31.875 30.0734 32.2701 30.7766 32.9734C31.4799 33.6766 31.875 34.6304 31.875 35.625V39.375" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
                },
            ],
        }
    },
    computed: {
        currentStepKey() {
            if (this.currentStep === 1) return 'integration'
            if (this.currentStep === 2) return this.selectedWidget?.id === 'activite' ? 'activityPage' : 'activitySelection'
            return 'integration'
        },
        iframeSrc() {
            if (!this.selectedWidget) return null

            if (this.selectedWidget.id === 'activite') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }

                if (this.selectedItem.isCombo) {
                    return `${this.url_front}/discover/combos/${this.selectedItem.label}?id=${this.selectedItem.originalId}`
                } else {
                    return `${this.url_front}/discover/reservation/${this.selectedItem.slug}`
                }
            }
            return this.selectedWidget.url_front
        },
        buttonText() {
            if (!this.selectedWidget) return ''
            if (this.selectedWidget.needsActivitySelection) {
                return this.currentStep === 1 ? this.$t('widgetIntegration.next') : this.$t('widgetIntegration.generateCode')
            }
            return this.$t('widgetIntegration.generateCode')
        },
        isBackButtonVisible() {
            return this.currentStep === 2 && this.selectedWidget && this.selectedWidget.needsActivitySelection
        },
        isActionButtonVisible() {
            return !!this.selectedWidget
        },
        selectedItem() {
            if (!this.selectedActivityId) {
                return null
            }
            return this.activities.find((item) => item.value === this.selectedActivityId) || null
        },
        actionKeys() {
            const isCombo = this.selectedItem && this.selectedItem.isCombo
            return {
                modify: isCombo ? 'widgetIntegration.modifyCombo' : 'widgetIntegration.modifyActivity',
                create: isCombo ? 'widgetIntegration.createNewCombo' : 'widgetIntegration.createNewActivity',
            }
        },
    },
    watch: {
        selectedActivityId(newActivityId, oldActivityId) {
            if (
                newActivityId &&
                newActivityId !== oldActivityId &&
                this.selectedWidget &&
                this.selectedWidget.id === 'activite'
            ) {
                this.checkActivityValidityAndUpdateIframe()
            }
        },
        iframeSrc(newUrl, oldUrl) {
            if (newUrl && newUrl !== oldUrl) {
                this.isLoadingIframe = true
            }
        },
    },
    created() {
        // Set language from establishment
        if (this.lang && this.$i18n) {
            this.$i18n.locale = this.lang
        }

        if (this.widgets.length > 0) {
            this.selectWidget(this.widgets[0])
        }
    },
    mounted() {
        window.addEventListener('focus', this.handleWindowFocus)
    },
    beforeDestroy() {
        window.removeEventListener('focus', this.handleWindowFocus)
    },
    methods: {
        selectWidget(widget) {
            console.log('selectWidget', widget)
            this.selectedWidget = widget
            this.currentStep = 1
            this.selectedActivityId = null
            this.activityIsValid = true
            this.activityError = null

            if (widget.id === 'activite') {
                this.fetchActivities()
            }
        },
        async fetchActivities() {
            this.submitting = true
            try {
                const response = await axios.get('/back-office/prestations/category/2')
                const regularActivities = response.data
                    .map((activity) => ({
                        label: activity.name,
                        value: `activity-${activity.id}`,
                        originalId: activity.id,
                        slug: activity.slug,
                        archiver: activity.archiver,
                        subcategory: activity.subcategory,
                        visible_online: activity.visible_online,
                        status: activity.status || (activity.visible_online ? 'Publiée' : null),
                        isCombo: false,
                    }))
                    .filter(
                        (activity) =>
                            !activity.subcategory ||
                            !['simpleTicket', 'simple-ticket', 'anniversaire', 'event', 'events', 'Events'].includes(
                                activity.subcategory.slug
                            )
                    )

                const comboListResponse = await axios.get('/api/v1/activite/combo-list?get-all=1')
                const combos = comboListResponse.data.data.map((combo) => ({
                    label: combo.name,
                    value: `combo-${combo.id}`,
                    originalId: combo.id,
                    // Combos fetched from combo-list are always published
                    status: combo.status || 'Publiée',
                    isCombo: true,
                }))
                this.activities = [...regularActivities, ...combos]

                // Set the first activity as selected by default if it exists
                if (this.activities.length > 0 && !this.selectedActivityId) {
                    this.selectedActivityId = this.activities[0].value
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_activities_error'))
            } finally {
                this.submitting = false
            }
        },
        handleActionButtonClick() {
            if (this.selectedWidget.needsActivitySelection) {
                if (this.currentStep === 1) {
                    this.currentStep = 2
                    this.fetchActivities()
                } else {
                    this.generateCode()
                }
            } else {
                this.generateCode()
            }
        },
        handleBackClick() {
            this.currentStep = 1
        },
        async generateCode() {
            if (!this.iframeSrc) {
                alert(this.$t('widgetIntegration.completeConfiguration'))
                return
            }

            // Check activity validity for 'activite' widget
            if (this.selectedWidget.id === 'activite') {
                this.submitting = true
                const validity = await this.checkActivityValidity()
                this.submitting = false
                if (!validity.isValid) {
                    alert(validity.error)
                    return
                }
            }

            this.generatedCodeSnippet = `
    <iframe
      src="${this.iframeSrc}"
      style="width:100%; height:100vh; border:none;"
      loading="eager"
      scrolling="yes"
    ></iframe>
      `.trim()

            this.isModalVisible = true
        },
        onIframeLoad() {
            this.isLoadingIframe = false
        },
        handleWindowFocus() {
            if (this.currentStep === 2) {
                console.log('Window focused, refreshing activities...')
                this.fetchActivities()
            }
        },

        handleModifyClick(event) {
            if (!this.selectedItem) {
                event.preventDefault()
                return
            }
            // Let the horizontal link target="_blank" handle the opening
            // but we can add tracking or additional logic here if needed.
        },
        handleCreateClick() {
            // Always redirect to experience create page via direct link
        },
        async checkActivityValidity() {
            if (!this.selectedActivityId || !this.selectedWidget || this.selectedWidget.id !== 'activite') {
                return { isValid: true, error: null }
            }

            const activity = this.selectedItem

            if (!activity) {
                return { isValid: false, error: 'Activité non trouvée' }
            }
            try {
                let apiUrl
                if (activity.isCombo === false) {
                    apiUrl = `${this.url_back}/api/v1/activite/${activity.slug}`
                } else {
                    apiUrl = `${this.url_back}/api/v1/activite/combo/${activity.originalId}`
                }
                const response = await axios.get(apiUrl)

                if (response.status === 200 && response.data) {
                    return { isValid: true, error: null }
                } else {
                    return { isValid: false, error: "L'activité n'est pas accessible" }
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.verify_activity_error'))
                console.error('Error checking activity validity:', error)
                return { isValid: false, error: "Erreur lors de la vérification de l'activité" }
            }
        },
        async checkActivityValidityAndUpdateIframe() {
            this.activityIsValid = false
            this.isLoadingIframe = true

            const validity = await this.checkActivityValidity()
            this.activityIsValid = validity.isValid
            this.activityError = validity.error

            this.isLoadingIframe = false
        },
        getEditUrl(item) {
            if (!item) return '#'
            const isPublished = item.status === 1 || (typeof item.status === 'string' && item.status.toLowerCase() === 'publiée')
            if (!item.isCombo) {
                // experience type
                return isPublished
                    ? `/back-office/v3/experience/edit/${item.originalId}/experience`
                    : `/back-office/v3/experience/create/${item.originalId}/experience`
            } else {
                // combo / pack type
                return isPublished
                    ? `/back-office/v3/package/edit/${item.originalId}`
                    : `/back-office/v3/package/create/${item.originalId}`
            }
        },
    },
}
</script>

<style scoped>
body {
    font-family: 'Poppins', sans-serif;
}

.bg-light {
    background-color: #f7f8fc !important;
}

.highlight {
    background-color: #ffdd59;
    padding: 0 0.5rem;
    border-radius: 8px;
}

.widget-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border-width: 2px !important;
    margin-bottom: 1.5rem;
    border-radius: 12px !important;
}

.widget-card:hover {
    border-color: #7d2dbd80 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.widget-card.active-card {
    border-color: #7d2dbd !important;
    background-color: rgb(245 245 245 / 5%) !important;
    box-shadow: 0 4px 20px rgba(125, 45, 189, 0.1);
}

.widget-icon {
    padding-left: 1.5rem !important;
    padding-right: 1.5rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.widget-icon svg {
    width: 60px !important;
    height: 60px !important;
}

.btn-dark-blue {
    background-color: #192a56;
    color: white;
    font-weight: 600;
    padding: 0.7rem 3rem;
}

.btn-dark-blue:hover {
    background-color: #2c3e50;
    color: white;
}

.iframe-wrapper {
    min-height: 600px;
    transition: opacity 0.3s ease;
}

/*
@media (min-width: 992px) {
    .iframe-wrapper {
        min-height: auto;
    }
}*/

.iframe-wrapper.loading {
    opacity: 0.3;
}

.loading-overlay {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    background-color: rgba(255, 255, 255, 0.75) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10 !important;
}

.error-overlay {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    background-color: rgba(255, 255, 255, 0.9) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10 !important;
}

.activity-actions-box {
    border: 1px dotted #a4b0be;
    border-radius: 12px;
    padding: 1.25rem;
    background-color: #f8f9fa;
}

.action-link-wrapper a {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #192a56;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
}

.action-link-wrapper a span {
    text-decoration: underline;
}

.action-link-wrapper a:hover {
    color: #2c3e50;
}

.action-icon {
    color: #3e4254;
}

.disabled-link {
    color: #a4b0be !important;
    pointer-events: none;
    cursor: not-allowed;
}

.disabled-link span {
    text-decoration: none !important;
}

.header-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 28px;
    line-height: 20px;
    letter-spacing: 0%;
    margin-bottom: 0.5rem;
}

.header-description {
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
    font-size: 17px;
    line-height: 20px;
    letter-spacing: 0%;
    color: #6c757d;
}
</style>
